type StorageListener = (key: string, newValue: string | null) => void;

class Storage {
  private available: boolean;
  private storage: globalThis.Storage;
  private fallback: Map<string, string>;
  private listeners: Map<string, Set<StorageListener>>;

  constructor() {
    this.fallback = new Map();
    this.listeners = new Map();
    this.available = this.checkAvailability();
    this.storage = localStorage;

    if (this.available) {
      window.addEventListener('storage', this.handleStorageEvent);
    }
  }

  private checkAvailability(): boolean {
    try {
      const key = '__storage_test__';
      localStorage.setItem(key, '1');
      localStorage.removeItem(key);
      return true;
    } catch {
      return false;
    }
  }

  private handleStorageEvent = (event: StorageEvent) => {
    if (!event.key) return;
    const keyListeners = this.listeners.get(event.key);
    if (keyListeners) {
      keyListeners.forEach(cb => cb(event.key!, event.newValue));
    }
  };

  get isAvailable(): boolean {
    return this.available;
  }

  changeStorage(storage: globalThis.Storage): void {
    this.storage = storage;
  }

  getItem(key: string): string | null {
    if (this.available) {
      return this.storage.getItem(key);
    }
    return this.fallback.get(key) ?? null;
  }

  setItem(key: string, value: string): void {
    if (this.available) {
      this.storage.setItem(key, value);
    } else {
      this.fallback.set(key, value);
    }
  }

  removeItem(key: string): void {
    if (this.available) {
      this.storage.removeItem(key);
    } else {
      this.fallback.delete(key);
    }
  }

  getJSON<T = unknown>(key: string): T | null {
    const raw = this.getItem(key);
    if (raw === null) return null;
    try {
      return JSON.parse(raw) as T;
    } catch {
      return null;
    }
  }

  subscribe(key: string, listener: StorageListener): () => void {
    if (!this.listeners.has(key)) {
      this.listeners.set(key, new Set());
    }
    this.listeners.get(key)!.add(listener);

    return () => {
      const set = this.listeners.get(key);
      if (set) {
        set.delete(listener);
        if (set.size === 0) this.listeners.delete(key);
      }
    };
  }
}

export const storage = new Storage();
